// ═══════════════════════════════════════════════════════════════════════════
// StatementUpload — import an aggregator's exported statement / CSV into the
// Daily Log as one row per day (source='statement').
// ═══════════════════════════════════════════════════════════════════════════
// The operator downloads the sales/orders/payout export their platform gives
// them (Deliveroo Hub, Uber Eats Manager, Talabat, Zomato/Swiggy payout sheets…),
// drops it here, and Netrev:
//   1. parses the CSV in the browser (lib/csv — no upload of the raw file),
//   2. guesses which columns are the date / gross / net / orders / refunds /
//      ad spend (editable dropdowns — every export names them differently),
//   3. sums rows per day and shows a preview,
//   4. POSTs the per-day figures to /api/ingestion/statement, which writes
//      daily_logs rows with the workspace's rates applied (calcDaily).
// Re-uploading the same period replaces the earlier statement rows; manual
// entries are never touched. Gated with the rest of the ingestion hub
// (INGESTION_ENABLED). Self-contained styling, like ConnectPlatforms.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useMemo, useState } from "react";
import { parseCSV } from "../lib/csv";
import { API_BASE } from "../lib/api";
import { getCurrentSession } from "../data/auth";
import { track } from "../lib/track";

const FIELDS = [
  { key: "date",    label: "Date",                 required: true,
    synonyms: ["date", "order date", "day", "transaction date", "settlement date", "delivery date", "created", "created at", "period", "business date", "order placed"] },
  { key: "gross",   label: "Gross sales",          required: true,
    synonyms: ["gross", "gross sales", "total sales", "sales", "order value", "order total", "subtotal", "sub total", "gross amount", "item total", "total", "food value", "basket value", "sales value", "gross merchandise value", "gmv", "amount"] },
  { key: "net",     label: "Net sales (after discounts)",
    synonyms: ["net", "net sales", "net amount", "after discount", "net order value", "sales after discount", "discounted total", "net revenue"] },
  { key: "orders",  label: "Orders",
    synonyms: ["orders", "order count", "number of orders", "no of orders", "count", "completed orders", "total orders", "delivered orders"] },
  { key: "refunds", label: "Refunds",
    synonyms: ["refunds", "refund", "refund amount", "refunded", "customer refunds", "adjustments"] },
  { key: "adSpend", label: "Ad spend",
    synonyms: ["ad spend", "ads", "marketing", "cpc", "promotion cost", "advertising", "ad cost", "marketing spend", "sponsored", "boost"] },
];

const norm = (x) => String(x || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Best-guess column for each field: exact synonym first, then "contains".
export function guessMapping(headers) {
  const m = {};
  const normed = headers.map((h) => ({ h, n: norm(h) }));
  for (const f of FIELDS) {
    const syns = f.synonyms.map(norm);
    let hit = normed.find(({ n }) => syns.includes(n));
    if (!hit) hit = normed.find(({ n }) => syns.some((s) => s.length >= 4 && n.includes(s)));
    // never map two fields to the same column
    if (hit && !Object.values(m).includes(hit.h)) m[f.key] = hit.h;
  }
  return m;
}

// Tolerant number: "£1,234.50", "(12.00)" → -12, "1.234,50" (EU) handled when
// the last separator is a comma with exactly 2 decimals.
export function parseNum(v) {
  if (v == null) return 0;
  let s = String(v).trim();
  if (!s) return 0;
  const neg = /^\(.*\)$/.test(s) || /^-/.test(s);
  s = s.replace(/[()]/g, "");
  if (/,\d{2}$/.test(s) && !/\.\d{2}$/.test(s)) s = s.replace(/\./g, "").replace(",", ".");
  s = s.replace(/[^0-9.]/g, "");
  const n = parseFloat(s);
  if (!Number.isFinite(n)) return 0;
  return neg ? -Math.abs(n) : n;
}

const MONTHS = { jan:1, feb:2, mar:3, apr:4, may:5, jun:6, jul:7, aug:8, sep:9, sept:9, oct:10, nov:11, dec:12 };
const ymd = (y, m, d) => {
  if (!(y >= 2000 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31)) return null;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};
// Accepts 2026-08-14, 2026-08-14T10:00, 14/08/2026, 14-08-2026, 08/14/2026
// (when dayFirst=false), 14 Aug 2026, Aug 14, 2026, 14-Aug-26.
export function parseDate(v, dayFirst = true) {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return ymd(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/);
  if (m) {
    let y = +m[3]; if (y < 100) y += 2000;
    const a = +m[1], b = +m[2];
    if (a > 12 && b <= 12) return ymd(y, b, a);          // unambiguous DD/MM
    if (b > 12 && a <= 12) return ymd(y, a, b);          // unambiguous MM/DD
    return dayFirst ? ymd(y, b, a) : ymd(y, a, b);
  }
  m = s.match(/^(\d{1,2})[ -]([A-Za-z]{3,9})[ -,]*(\d{2,4})/);
  if (m) { let y = +m[3]; if (y < 100) y += 2000; const mo = MONTHS[m[2].slice(0, 3).toLowerCase()]; return mo ? ymd(y, mo, +m[1]) : null; }
  m = s.match(/^([A-Za-z]{3,9})[ -](\d{1,2})[ ,-]+(\d{2,4})/);
  if (m) { let y = +m[3]; if (y < 100) y += 2000; const mo = MONTHS[m[1].slice(0, 3).toLowerCase()]; return mo ? ymd(y, mo, +m[2]) : null; }
  const d = new Date(s);
  if (!isNaN(d.getTime())) return ymd(d.getFullYear(), d.getMonth() + 1, d.getDate());
  return null;
}

// Sum rows per day. Returns { days: [{date, gross, net, orders, refunds, adSpend, rows}], skipped }.
export function aggregate(rows, mapping, dayFirst) {
  const byDay = new Map();
  let skipped = 0;
  for (const r of rows) {
    const date = parseDate(r[mapping.date], dayFirst);
    const gross = mapping.gross ? parseNum(r[mapping.gross]) : 0;
    if (!date || !mapping.gross) { skipped++; continue; }
    const cur = byDay.get(date) || { date, gross: 0, net: 0, hasNet: false, orders: 0, hasOrders: false, refunds: 0, hasRefunds: false, adSpend: 0, rows: 0 };
    cur.gross += gross;
    if (mapping.net) { cur.net += parseNum(r[mapping.net]); cur.hasNet = true; }
    if (mapping.orders) { const o = parseNum(r[mapping.orders]); cur.orders += o || 0; cur.hasOrders = true; }
    else { cur.orders += 1; cur.hasOrders = true; } // one row = one order when there is no orders column
    if (mapping.refunds) { cur.refunds += parseNum(r[mapping.refunds]); cur.hasRefunds = true; }
    if (mapping.adSpend) cur.adSpend += parseNum(r[mapping.adSpend]);
    cur.rows += 1;
    byDay.set(date, cur);
  }
  const days = [...byDay.values()].sort((a, b) => (a.date < b.date ? -1 : 1)).map((d) => ({
    date: d.date,
    gross: +d.gross.toFixed(2),
    net: d.hasNet ? +Math.min(d.net, d.gross).toFixed(2) : +d.gross.toFixed(2),
    orders: d.hasOrders ? Math.round(d.orders) : null,
    refunds: d.hasRefunds ? +Math.abs(d.refunds).toFixed(2) : null,
    adSpend: +Math.abs(d.adSpend).toFixed(2),
    rows: d.rows,
  }));
  return { days, skipped };
}

export default function StatementUpload({ wsId, platforms = [], onImported }) {
  const [platform, setPlatform] = useState(platforms[0]?.platform || "");
  const [custom, setCustom] = useState("");
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState(null);      // { headers, rows }
  const [mapping, setMapping] = useState({});
  const [dayFirst, setDayFirst] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const agg = useMemo(() => (parsed ? aggregate(parsed.rows, mapping, dayFirst) : null), [parsed, mapping, dayFirst]);
  const chosenPlatform = platform === "__other" ? custom.trim() : platform;

  async function onFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setNotice(null);
    try {
      const text = await file.text();
      const p = parseCSV(text);
      if (!p.headers.length || !p.rows.length) { setNotice({ kind: "error", text: "That file has no rows. Export a CSV from your platform's sales or orders report and try again." }); return; }
      setFileName(file.name);
      setParsed(p);
      setMapping(guessMapping(p.headers));
    } catch (err) {
      setNotice({ kind: "error", text: `Couldn't read the file: ${err.message}` });
    }
  }

  async function importDays() {
    if (!agg || !agg.days.length || !chosenPlatform) return;
    setBusy(true); setNotice(null);
    try {
      const session = await getCurrentSession();
      const token = session?.access_token;
      const res = await fetch(`${API_BASE}/api/ingestion/statement`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ workspaceId: wsId, platform: chosenPlatform, rows: agg.days.map(({ date, gross, net, orders, refunds, adSpend }) => ({ date, gross, net, orders, refunds, adSpend })) }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error((body && body.error) || `Import failed (${res.status})`);
      track("statement_uploaded", { platform: chosenPlatform, days: body?.written || agg.days.length });
      setNotice({ kind: "ok", text: `Imported ${body?.written ?? agg.days.length} day${(body?.written ?? agg.days.length) === 1 ? "" : "s"} of ${chosenPlatform} sales into your Daily Log.` });
      setParsed(null); setFileName(""); setMapping({});
      if (onImported) onImported(body);
    } catch (err) {
      setNotice({ kind: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  }

  const missing = FIELDS.filter((f) => f.required && !mapping[f.key]);
  const preview = agg ? agg.days.slice(0, 7) : [];
  const total = agg ? agg.days.reduce((n, d) => n + d.gross, 0) : 0;

  return (
    <div style={{ marginTop: 26, border: "1px solid #e3e8e4", borderRadius: 12, background: "#fff", padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#1c2320" }}>Upload a statement instead</div>
          <div style={{ fontSize: 12.5, color: "#6d7a74", marginTop: 3, maxWidth: 560, lineHeight: 1.5 }}>
            No extension needed. Export the sales / orders / payout report from your platform as CSV and drop it here — Netrev sums it per day and applies your rates. Weekly is fine; re-uploading a period replaces it.
          </div>
        </div>
      </div>

      {notice && (
        <div style={{ marginTop: 12, padding: "9px 12px", borderRadius: 9, fontSize: 13, background: notice.kind === "ok" ? "#e6f4ec" : "#fbe6e2", color: notice.kind === "ok" ? "#0a7d4b" : "#a3372a", border: `1px solid ${notice.kind === "ok" ? "#bfe3cd" : "#f0c8c0"}` }}>{notice.text}</div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginTop: 14 }}>
        <label style={lbl}>Platform
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} style={inp}>
            {platforms.map((p) => <option key={p.platform} value={p.platform}>{p.label || p.platform}</option>)}
            <option value="__other">Other…</option>
          </select>
        </label>
        {platform === "__other" && (
          <label style={lbl}>Platform name
            <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. Careem Food" style={inp} />
          </label>
        )}
        <label style={lbl}>CSV export
          <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ ...inp, padding: "6px 8px" }} />
        </label>
      </div>

      {parsed && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12.5, color: "#33413b", marginBottom: 8 }}>
            <b>{fileName}</b> · {parsed.rows.length} rows · {parsed.headers.length} columns. Check the column matches below — every platform names them differently.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 8 }}>
            {FIELDS.map((f) => (
              <label key={f.key} style={lbl}>
                {f.label}{f.required ? " *" : ""}
                <select value={mapping[f.key] || ""} onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value || undefined })} style={{ ...inp, borderColor: f.required && !mapping[f.key] ? "#d0553f" : "#cfd8d1" }}>
                  <option value="">— not in this file —</option>
                  {parsed.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </label>
            ))}
            <label style={{ ...lbl, justifyContent: "flex-end", flexDirection: "row", alignItems: "center", gap: 8, paddingTop: 18 }}>
              <input type="checkbox" checked={dayFirst} onChange={(e) => setDayFirst(e.target.checked)} />
              <span>Dates are day-first (14/08/2026)</span>
            </label>
          </div>

          {missing.length > 0 ? (
            <div style={{ marginTop: 10, fontSize: 12.5, color: "#a3372a" }}>Pick the column for: {missing.map((f) => f.label).join(", ")}.</div>
          ) : agg && agg.days.length === 0 ? (
            <div style={{ marginTop: 10, fontSize: 12.5, color: "#a3372a" }}>No rows had a readable date — try the other date order, or pick a different date column.</div>
          ) : agg && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12.5, color: "#33413b", marginBottom: 6 }}>
                <b>{agg.days.length} day{agg.days.length === 1 ? "" : "s"}</b> from {agg.days[0].date} to {agg.days[agg.days.length - 1].date} · gross {total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{agg.skipped ? ` · ${agg.skipped} rows skipped (no date/amount)` : ""}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", fontSize: 12.5, minWidth: 420 }}>
                  <thead><tr>{["Date", "Gross", "Net", "Orders", "Refunds", "Ad spend", "Rows"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {preview.map((d) => (
                      <tr key={d.date}>
                        <td style={td}>{d.date}</td><td style={tdNum}>{d.gross.toFixed(2)}</td><td style={tdNum}>{d.net.toFixed(2)}</td>
                        <td style={tdNum}>{d.orders ?? "—"}</td><td style={tdNum}>{d.refunds == null ? "—" : d.refunds.toFixed(2)}</td><td style={tdNum}>{d.adSpend.toFixed(2)}</td><td style={tdNum}>{d.rows}</td>
                      </tr>
                    ))}
                    {agg.days.length > preview.length && <tr><td colSpan={7} style={{ ...td, color: "#8b9a92" }}>… and {agg.days.length - preview.length} more days</td></tr>}
                  </tbody>
                </table>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
                <button disabled={busy || !chosenPlatform} onClick={importDays}
                  style={{ padding: "9px 16px", borderRadius: 8, border: "1px solid #0d7a5f", background: "#0d7a5f", color: "#fff", fontSize: 13, fontWeight: 600, cursor: busy ? "default" : "pointer", fontFamily: "inherit", opacity: busy || !chosenPlatform ? 0.6 : 1 }}>
                  {busy ? "Importing…" : `Import ${agg.days.length} day${agg.days.length === 1 ? "" : "s"} into Daily Log`}
                </button>
                <button disabled={busy} onClick={() => { setParsed(null); setFileName(""); setMapping({}); }} style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid #cfd8d1", background: "#fff", color: "#6d7a74", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                {!chosenPlatform && <span style={{ fontSize: 12, color: "#a3372a" }}>Name the platform first.</span>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const lbl = { display: "flex", flexDirection: "column", gap: 4, fontSize: 11.5, fontWeight: 600, color: "#5b6773", minWidth: 160 };
const inp = { padding: "8px 10px", borderRadius: 8, border: "1px solid #cfd8d1", fontSize: 13, fontFamily: "inherit", color: "#1c2320", background: "#fff" };
const th = { textAlign: "left", padding: "6px 10px", borderBottom: "1px solid #e3e8e4", color: "#5b6773", fontWeight: 600 };
const td = { padding: "6px 10px", borderBottom: "1px solid #f0f3f1" };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };
